import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { EntityCombobox } from "@/components/EntityCombobox"

type CreditFormProps = {
  id: number | null
  role: string
  details: string | null
  person_id: number | null
  person_name: string | null
  creditable_type: string
  creditable_id: number | null
  creditable_name: string | null
}
type PageProps = {
  credit: CreditFormProps
  return_to?: string | null
  errors?: Record<string, string>
}

const ic = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-primary"

export default function CreditForm({ credit, return_to }: { credit: CreditFormProps; return_to?: string | null }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = credit.id === null
  const creditableLocked = !!(credit.creditable_type && credit.creditable_id && credit.creditable_name)

  const [form, setForm] = useState({
    role:              credit.role ?? "",
    details:           credit.details ?? "",
    person_id:         credit.person_id !== null ? String(credit.person_id) : "",
    person_name:       credit.person_name ?? "",
    creditable_type:   credit.creditable_type ?? "Recording",
    creditable_id:     credit.creditable_id !== null ? String(credit.creditable_id) : "",
    creditable_name:   credit.creditable_name ?? "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleCreditableTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, creditable_type: e.target.value, creditable_id: "", creditable_name: "" }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = return_to ? { credit: form, return_to } : { credit: form }
    if (isNew) router.post("/credits", payload)
    else       router.patch(`/credits/${credit.id}`, payload)
  }

  const cancelHref = return_to ?? "/"
  const title = isNew ? "New credit" : "Edit credit"

  return (
    <>
      <Head title={title}>
        <meta name="description" content={title} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={title} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6"><h1>{title}</h1></div>
        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">

          <div>
            <label className="block text-sm text-ink-muted mb-1">Person</label>
            <EntityCombobox
              type="Person"
              initialId={form.person_id}
              initialName={form.person_name}
              placeholder="Search people…"
              onSelect={(id, name) => setForm((prev) => ({ ...prev, person_id: id, person_name: name }))}
              inputClass={ic}
            />
            {errors.person && <p className="mt-1 text-xs text-red-600">{errors.person}</p>}
          </div>

          <div>
            <label className="block text-sm text-ink-muted mb-1">Role</label>
            <input name="role" type="text" value={form.role} onChange={handleChange} className={ic} />
            {errors.role && <p className="mt-1 text-xs text-red-600">{errors.role}</p>}
          </div>

          {creditableLocked ? (
            <div>
              <label className="block text-sm text-ink-muted mb-1">Credited on</label>
              <div className={`${ic} bg-stone-50 dark:bg-stone-800/50 text-ink-muted cursor-default flex items-center gap-2`}>
                <span className="flex-1">{credit.creditable_name}</span>
                <span className="text-xs text-stone-400 dark:text-stone-500">{credit.creditable_type}</span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm text-ink-muted mb-1">Credited on type</label>
                <select name="creditable_type" value={form.creditable_type} onChange={handleCreditableTypeChange} className={ic}>
                  <option value="Recording">Recording</option>
                  <option value="Band">Band</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-ink-muted mb-1">Credited on</label>
                <EntityCombobox
                  key={form.creditable_type}
                  type={form.creditable_type}
                  initialId={form.creditable_id}
                  initialName={form.creditable_name}
                  placeholder={form.creditable_type === "Recording" ? "Search recordings…" : "Search bands…"}
                  onSelect={(id, name) => setForm((prev) => ({ ...prev, creditable_id: id, creditable_name: name }))}
                  inputClass={ic}
                />
                {errors.creditable && <p className="mt-1 text-xs text-red-600">{errors.creditable}</p>}
              </div>
            </>
          )}

          <div>
            <label className="block text-sm text-ink-muted mb-1">Details</label>
            <textarea name="details" rows={3} value={form.details} onChange={handleChange} className={ic} />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create credit" : "Update credit"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
